"use client";

import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { BookmarkPlus, Loader2, Trash2 } from "lucide-react";
import { PostHogCaptureLink } from "@/components/posthog-capture-link";
import { readJsonResponse } from "@/lib/client-http";

interface ReplayNote {
  id: number;
  roundNumber: number;
  tick: number;
  note: string;
  createdAt: string;
}

interface ReplayNotesPanelProps {
  artifactId: number | null;
  roundNumber: number;
  tick: number;
}

export function ReplayNotesPanel({
  artifactId,
  roundNumber,
  tick,
}: ReplayNotesPanelProps) {
  const [notes, setNotes] = useState<ReplayNote[]>([]);
  const [draft, setDraft] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!artifactId) {
      setNotes([]);
      setUpgradeRequired(false);
      setError(null);
      return;
    }

    let cancelled = false;
    async function loadNotes() {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/replay-notes?artifactId=${artifactId}`);
        const body = await readJsonResponse<{
          notes?: ReplayNote[];
          upgradeRequired?: boolean;
          error?: string;
        }>(response);

        if (!response.ok) {
          throw new Error(body.error ?? "Failed to load replay notes");
        }
        if (!cancelled) {
          setNotes(body.notes ?? []);
          setUpgradeRequired(Boolean(body.upgradeRequired));
        }
      } catch (nextError) {
        if (!cancelled) {
          setError(
            nextError instanceof Error
              ? nextError.message
              : "Failed to load replay notes"
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadNotes();
    return () => {
      cancelled = true;
    };
  }, [artifactId]);

  async function saveNote() {
    if (!artifactId || !draft.trim()) {
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const response = await fetch("/api/replay-notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          artifactId,
          roundNumber,
          tick,
          note: draft,
        }),
      });
      const body = await readJsonResponse<{
        note?: ReplayNote;
        upgradeRequired?: boolean;
        error?: string;
      }>(response);

      if (!response.ok || !body.note) {
        if (body.upgradeRequired) {
          setUpgradeRequired(true);
        }
        throw new Error(body.error ?? "Failed to save replay note");
      }

      posthog.capture("replay_note_created_client", {
        artifact_id: artifactId,
        round_number: roundNumber,
      });
      setNotes((current) => [...current, body.note!]);
      setDraft("");
    } catch (nextError) {
      setError(
        nextError instanceof Error ? nextError.message : "Failed to save replay note"
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteNote(id: number) {
    setDeletingId(id);
    setError(null);
    try {
      const response = await fetch("/api/replay-notes", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      const body = await readJsonResponse<{ success?: boolean; error?: string }>(
        response
      );

      if (!response.ok || !body.success) {
        throw new Error(body.error ?? "Failed to delete replay note");
      }

      setNotes((current) => current.filter((note) => note.id !== id));
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "Failed to delete replay note"
      );
    } finally {
      setDeletingId(null);
    }
  }

  if (!artifactId) {
    return null;
  }

  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <BookmarkPlus className="h-4 w-4 text-primary-soft" />
            <h3 className="text-sm font-semibold text-white">Replay notes</h3>
          </div>
          <p className="mt-1 text-xs leading-5 text-neutral-400">
            Bookmark the current round and tick with review notes.
          </p>
        </div>

        {upgradeRequired ? (
          <PostHogCaptureLink
            href="/pricing"
            eventName="subscription_cta_clicked"
            eventProperties={{
              location: "replay_notes_panel",
              route_group: "replay",
              is_signed_in: true,
              current_plan_key: "free",
            }}
            className="inline-flex shrink-0 rounded-xl border border-primary/26 bg-primary/12 px-3 py-2 text-xs font-semibold text-primary-soft transition hover:bg-primary/18"
          >
            Save notes with cloud plans
          </PostHogCaptureLink>
        ) : null}
      </div>

      {upgradeRequired ? (
        <p className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs leading-5 text-neutral-300">
          Notes are a paid replay-library feature because they remain attached
          to saved cloud replays.
        </p>
      ) : (
        <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_auto]">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            maxLength={1000}
            placeholder={`Round ${roundNumber}, tick ${tick}`}
            className="min-h-20 resize-none rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:border-primary/45 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => void saveNote()}
            disabled={isSaving || !draft.trim()}
            className="inline-flex min-w-36 items-center justify-center rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary-bright disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Bookmark"}
          </button>
        </div>
      )}

      {error ? (
        <p className="mt-3 text-xs text-red-200">{error}</p>
      ) : null}

      {isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-neutral-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading notes
        </div>
      ) : notes.length > 0 ? (
        <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {notes.map((item) => (
            <article
              key={item.id}
              className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-semibold text-primary-soft">
                  Round {item.roundNumber} · tick {item.tick}
                </p>
                <button
                  type="button"
                  onClick={() => void deleteNote(item.id)}
                  disabled={deletingId === item.id}
                  className="rounded-md p-1 text-neutral-500 transition hover:bg-white/10 hover:text-red-200 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Delete replay note"
                >
                  {deletingId === item.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-neutral-200">
                {item.note}
              </p>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}
